import { useState, useEffect } from 'react';
import { useDebounceValue } from 'usehooks-ts';
import { TextField, Autocomplete } from '@mui/material';

import { joinStrings } from 'utils/helpers';
import { useListQuery } from 'store/api/user';

import { UserAutocompleteProps, UserAutocompleteOption } from './types';

const getUserOptionLabel = (option: UserAutocompleteOption) => {
  const name =
    option.fullName ||
    joinStrings([option.firstName, option.lastName, option.guestId], ' ', '');

  return name && option.email ? `${name} (${option.email})` : name;
};

export const UserAutocomplete = ({
  role,
  value,
  tenantId,
  disabled,
  onChange,
  searchLimit = 20,
  minSearchLength = 1,
  label = 'Select user',
  noOptionsText = 'No users found',
  placeholder = 'Type to search...',
}: UserAutocompleteProps) => {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  // Keep search text separate so selecting a user does not search by the display label.
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearch] = useDebounceValue(searchValue, 300);

  const trimmedSearchValue = searchValue.trim();
  const search = debouncedSearch.trim();
  const isSearchReady =
    trimmedSearchValue.length >= minSearchLength &&
    search.length >= minSearchLength;
  const isDebouncing =
    trimmedSearchValue !== search &&
    trimmedSearchValue.length >= minSearchLength;

  const { data, isError, isFetching } = useListQuery(
    {
      role,
      search,
      offset: 0,
      limit: searchLimit,
      ...(tenantId && { tenantId }),
    },
    { skip: !isSearchReady }
  );

  const fetchedOptions = isSearchReady && !isError ? (data?.data ?? []) : [];
  const options =
    !isError &&
    value &&
    !fetchedOptions.some((option) => option.id === value.id)
      ? [value, ...fetchedOptions]
      : fetchedOptions;
  const isLoading = isDebouncing || isFetching;
  const helperText = isError ? 'Unable to load users' : undefined;

  useEffect(() => {
    setOpen(false);
    setInputValue('');
    setSearchValue('');
  }, [tenantId, role]);

  useEffect(() => {
    setInputValue(value ? getUserOptionLabel(value) : '');
    setSearchValue('');
  }, [value]);

  return (
    <Autocomplete<UserAutocompleteOption>
      open={open}
      value={value}
      options={options}
      disabled={disabled}
      clearOnBlur={false}
      loading={isLoading}
      inputValue={inputValue}
      filterOptions={(x) => x}
      onClose={() => setOpen(false)}
      getOptionLabel={getUserOptionLabel}
      isOptionEqualToValue={(option, val) => option.id === val.id}
      noOptionsText={isError ? 'Unable to load users' : noOptionsText}
      onOpen={() => {
        if (trimmedSearchValue.length >= minSearchLength) {
          setOpen(true);
        }
      }}
      onChange={(_, user) => {
        setOpen(false);
        setInputValue(user ? getUserOptionLabel(user) : '');
        setSearchValue('');
        onChange(user);
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          error={isError}
          helperText={helperText}
          placeholder={placeholder}
        />
      )}
      onInputChange={(_, val, reason) => {
        setInputValue(val);
        if (reason === 'clear' || reason === 'reset') {
          setSearchValue('');
          setOpen(false);
        } else if (reason === 'input') {
          const trimmed = val.trim();
          setSearchValue(val);
          if (trimmed.length >= minSearchLength) {
            setOpen(true);
          } else {
            setOpen(false);
          }
        }
      }}
    />
  );
};

export type { UserAutocompleteOption };
